import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import StudentResultTable from './StudentResultTable';
import { buildCommentsSave, formatCommentDate, previousComments } from '../utils/teacherComments';
import StudentTeacherNotes from './StudentTeacherNotes';
import FamilyCommentsHeading from './FamilyCommentsHeading';
import { studentNotesOf, STUDENT_NOTES_FIELD } from '../utils/studentNotes';
import { BOY_NOTES_TYPE } from '../constants/boyItems';
import { IS_DEMO } from '../constants/featureFlags';

/**
 * Student details popup. Printing happens IN PLACE (window.print() with the
 * print stylesheet below) — no navigation to /teacher-report; that page
 * remains only for TLS deep links (Skill Gap Finder) and old bookmarks.
 * The print header (name / academic year / teacher) and the Teacher Comments
 * block are print-only elements, so the on-screen popup stays unchanged.
 *
 * The popup is rendered as a PORTAL directly under <body>: during print,
 * everything else under body (the app root — Dashboard class lists, Results
 * tables) is display:none'd, so the report is the ONLY thing in print layout.
 * Relying on visibility:hidden or per-page CSS left host-page content leaking
 * into printouts (Dashboard) and made the browser's shrink-to-fit scale vary.
 */
const NOTES_LOAD_TIMEOUT_MS = 15000;

/** An admin's "view as" session (sessionStorage flag set by impersonation): notes are read-only — an entry would carry the other user's name, and Logger skips ordinary audit events then (only result corrections are recorded, marked viewingAs — v3.98.1). */
function viewingAsAnotherUser() {
    try {
        return typeof window !== 'undefined' && window.sessionStorage.getItem('isImpersonating') === 'true';
    } catch (e) {
        return false;
    }
}

/** The 4K Readiness note (v3.46+ notes doc) among this student's results, or ''. */
function readinessNoteOf(assessments) {
    const docNote = (assessments || []).find((a) => a && a.testType === BOY_NOTES_TYPE && typeof a.note === 'string' && a.note.trim());
    return docNote ? docNote.note.trim() : '';
}

function StudentDetailsModal({ student, assessments, letterColumns, onClose, onRefresh, academicYearLabel, user, readinessNote }) {
    const [teacherComments, setTeacherComments] = useState('');
    const [commentDate, setCommentDate] = useState(null);
    const [commentsOpen, setCommentsOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    // Student-level teacher notes (v3.96.0) — screen only here, never on this
    // (family) report; v3.98.0: they print only on the Student Report's staff copy.
    const [noteEntries, setNoteEntries] = useState([]);
    // 'loading' until the student record is read; 'failed' when it cannot be
    // (or is not back in 15 s) — never a silent "No notes yet.".
    const [notesStatus, setNotesStatus] = useState('loading');
    const lastSavedRef = useRef('');
    const typingRef = useRef(false);

    useEffect(() => {
        const applyCommentFields = (data) => {
            const saved = data?.teacherComments || '';
            setTeacherComments(saved);
            setCommentDate(data?.teacherCommentsUpdatedAt || null);
            setHistory(Array.isArray(data?.teacherCommentsHistory) ? data.teacherCommentsHistory : []);
            setCommentsOpen(Boolean(saved.trim()));
            lastSavedRef.current = saved;
        };
        const applyNotes = (data) => setNoteEntries(studentNotesOf(data));
        applyNotes(student);
        applyCommentFields(student);
        setHistoryOpen(false);
        typingRef.current = false;

        // The parent page's copy can be stale (saved in another tab, edited in
        // Firestore) — refresh from the doc so the box and the history are
        // current. Skip the update if the teacher already started typing.
        if (!student) return undefined;
        let cancelled = false;
        setNotesStatus('loading');
        const notesTimer = setTimeout(() => {
            if (!cancelled) setNotesStatus((s) => (s === 'loading' ? 'failed' : s));
        }, NOTES_LOAD_TIMEOUT_MS);
        getDoc(doc(db, 'students', student.studentId || student.id))
            .then((snap) => {
                if (cancelled) return;
                clearTimeout(notesTimer);
                if (!snap.exists()) { setNotesStatus('failed'); return; }
                setNotesStatus('loaded');
                const fresh = snap.data();
                // Notes are their own list (added / deleted one entry at a
                // time), so they refresh even while a comment is being typed.
                Object.assign(student, { [STUDENT_NOTES_FIELD]: fresh[STUDENT_NOTES_FIELD] || [] });
                applyNotes(fresh);
                if (typingRef.current) return;
                Object.assign(student, {
                    teacherComments: fresh.teacherComments || '',
                    teacherCommentsUpdatedAt: fresh.teacherCommentsUpdatedAt || null,
                    teacherCommentsHistory: fresh.teacherCommentsHistory || [],
                });
                applyCommentFields(fresh);
            })
            .catch(() => {
                // Keep the prop copy of the comments; say the notes could not be read.
                if (cancelled) return;
                clearTimeout(notesTimer);
                setNotesStatus('failed');
            });
        return () => { cancelled = true; clearTimeout(notesTimer); };
    }, [student]);

    // Set by the Teacher notes section while a note is unsaved, a delete is
    // being asked or a write is in flight: Escape must not close the pop-up then.
    const notesGuard = useRef(false);
    const setNotesGuard = useCallback((on) => { notesGuard.current = on; }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (notesGuard.current) return;
                onClose();
            }
        };

        // Add event listener when the modal mounts
        document.addEventListener('keydown', handleKeyDown);

        // Clean up the event listener when the modal unmounts
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    if (!student) return null;

    const saveComments = async () => {
        if (teacherComments === lastSavedRef.current) return;
        try {
            const studentRef = doc(db, 'students', student.studentId || student.id);
            // Merge against the FRESHEST doc, not the in-memory copy: the
            // same-day update-vs-append decision depends on the stored history,
            // and a stale copy (another tab, an out-of-band edit) would
            // silently overwrite entries (v3.32.1).
            let baseline = student;
            try {
                const snap = await getDoc(studentRef);
                if (snap.exists()) baseline = snap.data();
            } catch (freshErr) {
                // Offline etc. — fall back to the in-memory copy.
            }
            const fields = buildCommentsSave(baseline, teacherComments);
            await updateDoc(studentRef, fields);
            lastSavedRef.current = teacherComments;
            // Keep the parent page's copy of this student in sync so reopening
            // the popup (without a refetch) shows what was just saved.
            Object.assign(student, fields);
            setCommentDate(fields.teacherCommentsUpdatedAt);
            setHistory(fields.teacherCommentsHistory);
        } catch (err) {
            console.error('Could not save teacher comments:', err);
        }
    };

    const handlePrintReport = async () => {
        await saveComments();
        window.print();
    };

    return createPortal(
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-actions no-print">
                    <button className="modal-close" onClick={onClose}>X</button>
                    <div className="action-buttons" style={{ display: 'flex', alignItems: 'center' }}>
                        <button onClick={handlePrintReport} className="print-button" title="Print this report (add optional comments for families below first; teacher notes are not on this report)">🖨️ Print Report</button>
                    </div>
                </div>

                <div className="modal-header no-print">
                    <h2>Student: {student.name} | Teacher: {student.homeroomTeacher}</h2>
                </div>

                {/* Professional report header — print only */}
                <div className="print-report-header">
                    <h1>{student.name}</h1>
                    <h3>Literacy Assessment Progress</h3>
                    <p>
                        {academicYearLabel ? `Academic Year: ${academicYearLabel} | ` : ''}Teacher: {student.homeroomTeacher}
                    </p>
                </div>

                <div className="modal-table-container">
                    <StudentResultTable student={student} assessments={assessments} letterColumns={letterColumns} onRefresh={onRefresh} user={user} />
                </div>

                <div className={`comments-section${!teacherComments.trim() ? ' hide-comments-on-print' : ''}`}>
                    {!commentsOpen ? (
                        <button
                            type="button"
                            className="no-print add-comments-toggle"
                            onClick={() => setCommentsOpen(true)}
                        >
                            + Add comments for families
                        </button>
                    ) : (
                        <>
                            {/* Screen: "Comments for families"; paper: "Teacher Comments" (unchanged). */}
                            <FamilyCommentsHeading className="comments-title" hintClassName="comments-printed-hint" />
                            <textarea
                                className="no-print comments-input"
                                value={teacherComments}
                                onChange={(e) => { typingRef.current = true; setTeacherComments(e.target.value); }}
                                onBlur={saveComments}
                                placeholder="Enter professional comments for the parent — saved automatically (with today's date) and printed on the report..."
                            />
                            {commentDate && teacherComments.trim() && (
                                <div className="no-print comment-saved-date">Comment date: {formatCommentDate(commentDate)}</div>
                            )}
                        </>
                    )}
                    {previousComments(history).length > 0 && (
                        <div className="no-print">
                            <button
                                type="button"
                                className="history-toggle"
                                onClick={() => setHistoryOpen((v) => !v)}
                            >
                                {historyOpen
                                    ? 'Hide previous comments'
                                    : `🕘 View previous comments (${previousComments(history).length})`}
                            </button>
                            {historyOpen && (
                                <div className="comments-history">
                                    {previousComments(history).map((entry, i) => (
                                        <div key={`${entry.date || 'earlier'}-${i}`} className="history-entry">
                                            <div className="history-date">{formatCommentDate(entry.date) || 'Earlier'}</div>
                                            <p>{entry.text}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Print: current comment with its date */}
                    <div className="print-only-comments">
                        {commentDate && <div className="print-comment-date">{formatCommentDate(commentDate)}</div>}
                        <p>{teacherComments}</p>
                    </div>
                </div>

                {/* Teacher notes (v3.96.0): screen only — the section is .no-print. */}
                <StudentTeacherNotes
                    key={student.studentId || student.id}
                    studentId={student.studentId || student.id}
                    studentName={student.name}
                    entries={noteEntries}
                    user={user}
                    readOnly={IS_DEMO || !user || viewingAsAnotherUser()}
                    readOnlyReason={IS_DEMO
                        ? 'Notes cannot be changed on the demo.'
                        : (!user
                            ? 'Sign in to add notes.'
                            : 'Notes cannot be changed here while you are viewing as another user.')}
                    loadStatus={notesStatus}
                    readinessNote={readinessNote || readinessNoteOf(assessments)}
                    onEntriesChange={(next) => { Object.assign(student, { [STUDENT_NOTES_FIELD]: next }); }}
                    onGuardChange={setNotesGuard}
                />
            </div>
            <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 8px;
          max-width: 95vw;
          max-height: 90vh;
          overflow: auto;
          position: relative;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        .modal-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }
        .modal-close {
          font-size: 1.5rem;
          background: none;
          border: none;
          cursor: pointer;
          font-weight: bold;
        }
        .action-buttons button {
            margin-left: 10px;
            padding: 8px 12px;
            cursor: pointer;
            border: none;
            border-radius: 4px;
            font-weight: bold;
        }
        .print-button {
            background-color: #007bff;
            color: white;
        }
        .modal-header h2 {
            margin-top: 0;
        }
        .print-report-header {
            display: none;
        }
        .comments-section {
            margin-top: 15px;
            border-top: 2px solid #eee;
            padding-top: 10px;
        }
        .add-comments-toggle {
            background: none;
            border: 1px dashed #999;
            color: #555;
            padding: 8px 14px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.95rem;
        }
        .comments-title {
            margin: 0 0 10px 0;
        }
        .print-only-heading {
            display: none;
        }
        .comments-printed-hint {
            margin: -6px 0 8px 0;
            color: #64748b;
            font-size: 0.9rem;
        }
        .comments-input {
            width: 100%;
            min-height: 100px;
            padding: 10px;
            font-size: 1rem;
            border-radius: 4px;
            border: 1px solid #ccc;
            box-sizing: border-box;
        }
        .comment-saved-date {
            margin-top: 4px;
            font-size: 0.85rem;
            color: #64748b;
            font-style: italic;
        }
        .history-toggle {
            margin-top: 10px;
            background: none;
            border: none;
            color: #0284c7;
            cursor: pointer;
            font-size: 0.9rem;
            padding: 0;
        }
        .comments-history {
            margin-top: 8px;
            border-left: 3px solid #e2e8f0;
            padding-left: 12px;
        }
        .history-entry {
            margin-bottom: 10px;
        }
        .history-entry p {
            margin: 2px 0 0 0;
            white-space: pre-wrap;
            color: #334155;
        }
        .history-date {
            font-size: 0.8rem;
            color: #64748b;
            font-weight: bold;
        }
        .print-only-comments {
            display: none;
        }

        @media print {
            * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
            @page {
                size: landscape !important;
                margin: 0.25cm; /* Even smaller margins */
            }
            .no-print, .modal-close {
                display: none !important;
            }

            /* The popup is a direct child of <body> (portal). Remove EVERY
               other body child from print layout — the app root included —
               so host-page content (Dashboard class lists, Results tables)
               can neither print below the report nor distort the browser's
               shrink-to-fit scale. */
            body > *:not(.modal-overlay) {
                display: none !important;
            }

            /* Reset overlay to standard flow */
            .modal-overlay {
                position: static !important;
                width: 100%;
                height: auto;
                background: white;
                display: block; /* Use block to allow margin auto to work */
                padding: 0;
            }

            /* Professional report header */
            .print-report-header {
                display: block !important;
                text-align: center;
                margin-bottom: 10px;
                page-break-after: avoid;
            }
            .print-report-header h1 {
                margin: 0;
                font-size: 18pt;
                color: #000;
            }
            .print-report-header h3 {
                margin: 2px 0 0 0;
                font-weight: normal;
                font-size: 11pt;
                color: #444;
            }
            .print-report-header p {
                margin: 2px 0 0 0;
                font-style: italic;
                font-size: 9pt;
                color: #666;
            }

            .modal-content {
                box-shadow: none;
                max-width: 100%;
                max-height: none;
                width: 100%;
                padding: 0;
                overflow: visible !important;
                border: none;
                margin: 0;

                /* Remove scale to fix layout/pagination sync */
                transform: none;
            }

            body {
                background: white;
            }
            /* Style tags never render, but be safe */
            style {
                display: none !important;
            }

            /* Teacher comments print as a serif paragraph; the whole section
               is omitted when empty (hide-comments-on-print). */
            .print-only-comments {
                display: block !important;
            }
            .print-only-heading {
                display: inline !important;
            }
            .print-only-comments p {
                white-space: pre-wrap;
                overflow-wrap: break-word; /* never widen the page (shrink-to-fit) */
                font-family: serif;
                font-size: 11pt;
                margin-top: 2px;
            }
            .print-comment-date {
                font-style: italic;
                font-size: 9pt;
                color: #444;
            }
            .hide-comments-on-print {
                display: none !important;
            }
            .comments-section {
                page-break-inside: avoid;
            }
        }
      `}</style>
        </div>,
        document.body
    );
}

export default StudentDetailsModal;
